import { Component } from "@angular/core";
import {
  ViewController,
  NavParams,
  ToastController,
  LoadingController
} from "ionic-angular";
import { FhirService } from "../../app/services/fhir.service";
import * as PatientActions from "../../app/store/actions/patient.actions";
import { AppState } from "../../app/models/app.state";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { _i18n_str } from "../../assets/i18n/non-static-ids";
import { DiaprofilPatient } from "../../app/models/diaprofil-patient";
import {
  flatpickrConfig,
  flatpickrTimepointConfig,
  proTaskConsultationType,
  PRO_TASK_CONSULTATION_TYPE_EXT_URL,
  PRO_TASK_CONSULTATION_TIME_EXT_URL,
  proTaskCancellationReasons,
  PRO_TASK_CANCELLATION_REASON_SYSTEM_URL,
  proTaskConsultationTypeOptions,
  proTaskCancellationReasonOptions
} from "../../app/models/types";
import { AlertController } from "ionic-angular/components/alert/alert-controller";
import { UserState } from "../../app/store/reducers/user.reducer";
import { AdminService } from "../../app/services/admin.service";
import * as moment from "moment";

import {
  compare,
  AddOperation,
  RemoveOperation,
  ReplaceOperation,
  MoveOperation,
  CopyOperation,
  TestOperation,
  GetOperation
} from "fast-json-patch";
import { setTimeout } from "timers";

@Component({
  selector: "manage-task-modal",
  templateUrl: "manage-task.modal.html"
})
export class ManageTaskModal {
  patient: DiaprofilPatient;
  task: fhir.Task;
  user: UserState;
  modalType: "CREATE" | "EDIT";
  taskDate: string = null;
  taskDateObj: Date[] = null;
  consultationTimepointObj: Date[] = null;
  proTaskConsultationType: proTaskConsultationType = null;
  flatpickrConfig = flatpickrConfig;
  flatpickrTimepointConfig = flatpickrTimepointConfig;
  constructor(
    public viewCtrl: ViewController,
    public navParams: NavParams,
    public fhir: FhirService,
    public admin: AdminService,
    public toastCtrl: ToastController,
    public store: Store<AppState>,
    public loadingCtrl: LoadingController,
    public translate: TranslateService,
    private alertCtrl: AlertController
  ) {
    this.patient = (this.navParams.get("patient") || {}) as DiaprofilPatient;
    this.task = (this.navParams.get("task") || null) as fhir.Task;
    if (
      this.task !== null &&
      this.task.hasOwnProperty("extension") &&
      this.task.extension.length > 0
    ) {
      const consultationTypeExt = this.task.extension.find(
        ext => ext.url === PRO_TASK_CONSULTATION_TYPE_EXT_URL
      );
      if (consultationTypeExt !== undefined) {
        this.proTaskConsultationType = consultationTypeExt.valueCode as proTaskConsultationType;
      }
      const consultationTimeExt = this.task.extension.find(
        ext => ext.url === PRO_TASK_CONSULTATION_TIME_EXT_URL
      );
      if (consultationTimeExt !== undefined) {
        this.consultationTimepointObj = [
          moment(consultationTimeExt.valueTime, "HH:mm:ss").toDate()
        ];
      }
    }
    this.user = (navParams.get("user") || {}) as UserState;
    this.modalType = this.task !== null ? "EDIT" : "CREATE";
    if (this.task !== null) {
      const utcOffset = moment().utcOffset();
      this.flatpickrConfig.defaultDate = moment(
        this.task.restriction.period.end
      )
        .add(utcOffset)
        .toDate();
      if (
        Array.isArray(this.consultationTimepointObj) &&
        this.consultationTimepointObj.length > 0
      ) {
        this.flatpickrTimepointConfig.defaultDate = this.consultationTimepointObj[0];
      }
      if (this.task !== null && this.task.status !== "ready") {
        this.flatpickrConfig.minDate = "2019-06-01";
      } else {
        if (new Date() > new Date(this.task.restriction.period.end)) {
          this.flatpickrConfig.minDate = this.task.restriction.period.end;
        }
      }
    }
    if (
      this.modalType === "CREATE" &&
      this.admin.orgConfig.ffProTaskConsultationTypeExtEnabled
    ) {
      if (this.admin.orgConfig.ffProTaskConsultationTypeExtDefault !== null) {
        this.proTaskConsultationType = this.admin.orgConfig.ffProTaskConsultationTypeExtDefault;
      }
    }
  }

  dismiss($event) {
    this.viewCtrl.dismiss();
  }

  isNoDateEntered() {
    const hasNoDate = this.taskDateObj === null;
    if (this.admin.orgConfig.ffProTaskConsultationTypeExtEnabled) {
      if (this.modalType === "CREATE") {
        return hasNoDate || this.proTaskConsultationType === null;
      } else {
        // If we have proTaskConsultation type we always allow patch
        return false;
      }
    }
    return hasNoDate;
  }

  getTaskExtensions(): fhir.Extension[] {
    const exts: fhir.Extension[] = [];
    if (this.proTaskConsultationType !== null) {
      exts.push({
        url: PRO_TASK_CONSULTATION_TYPE_EXT_URL,
        valueCode: this.proTaskConsultationType
      });
    }
    if (this.consultationTimepointObj !== null) {
      const date = this.consultationTimepointObj[0];
      exts.push({
        url: PRO_TASK_CONSULTATION_TIME_EXT_URL,
        valueTime: moment(date).format("HH:mm:ss")
      });
    }
    return exts;
  }

  createTask($event) {
    const loading = this.loadingCtrl.create({
      content: this.translate.instant(
        _i18n_str("GENERIC_UI.CREATING_NEW_TASK")
      ),
      dismissOnPageChange: true
    });
    loading.present();
    this.taskDate = this.taskDateObj[0].toISOString();
    this.fhir
      .addTasks({
        patient_reference: this.patient.fhirId,
        requester_reference: this.user.userFhir.id,
        task_type: "DIAPROFIL",
        task_end_date: this.taskDate,
        return_only_parent_task: true,
        extensions: this.getTaskExtensions()
      })
      .subscribe(
        result => {
          loading.dismissAll();
          const activeTask = result.find(
            elem =>
              elem.resourceType === "Task" && !elem.hasOwnProperty("partOf")
          ) as fhir.Task;
          this.store.dispatch(
            new PatientActions.UpdateAction({
              fhirId: this.patient.fhirId,
              activeTask: activeTask,
              proTasks: [
                ...this.patient.proTasks,
                ...[
                  {
                    task: activeTask,
                    procedureRequest: null,
                    response: null
                  }
                ]
              ]
            })
          );
          this.viewCtrl.dismiss();
          const toast = this.toastCtrl.create({
            message: `Opgave oprettet!`,
            duration: 3000,
            position: "top",
            showCloseButton: true
          });
          toast.present();
        },
        // Errors will call this callback instead:
        err => {
          loading.dismissAll();
          const toast = this.toastCtrl.create({
            message: JSON.stringify(err),
            duration: 20000,
            position: "top",
            showCloseButton: true
          });
          toast.present();
        }
      );
  }

  handlePatch(
    patchOperations:
      | { op: string; path: string; value: any }[]
      | (
          | AddOperation<any>
          | RemoveOperation
          | ReplaceOperation<any>
          | MoveOperation
          | CopyOperation
          | TestOperation<any>
          | GetOperation<any>
        )[],
    patchedTask: fhir.Task,
    patchType: "EDIT" | "CANCEL"
  ) {
    const loading = this.loadingCtrl.create({
      content: this.translate.instant(_i18n_str("GENERIC_UI.PATCHING_TASK")),
      dismissOnPageChange: true
    });
    loading.present();
    this.fhir
      .patchResource({
        resourceId: this.task.id,
        resourceType: "Task",
        operations: patchOperations
      })
      .subscribe(
        result => {
          loading.dismissAll();
          this.viewCtrl.dismiss();
          if (patchType === "EDIT") {
            this.store.dispatch(
              new PatientActions.UpdateAction({
                fhirId: this.patient.fhirId,
                proTasks: this.patient.proTasks.map(proTask =>
                  proTask.task.id !== patchedTask.id
                    ? proTask
                    : {
                        task: patchedTask,
                        procedureRequest: proTask.procedureRequest,
                        response: proTask.response
                      }
                ),
                activeTask:
                  this.patient.activeTask !== null &&
                  this.patient.activeTask.id === patchedTask.id
                    ? patchedTask
                    : this.patient.activeTask
              })
            );
          } else {
            this.store.dispatch(
              new PatientActions.UpdateAction({
                fhirId: this.patient.fhirId,
                proTasks: this.patient.proTasks.filter(
                  proTask => proTask.task.id !== patchedTask.id
                ),
                activeTask:
                  this.patient.activeTask.id === patchedTask.id
                    ? null
                    : this.patient.activeTask
              })
            );
          }
          const toast = this.toastCtrl.create({
            message: `Opgave rettet!`,
            duration: 3000,
            position: "top",
            showCloseButton: true
          });
          toast.present();
        },
        // Errors will call this callback instead:
        err => {
          const toast = this.toastCtrl.create({
            message: JSON.stringify(err),
            duration: 20000,
            position: "top",
            showCloseButton: true
          });
          toast.present();
        }
      );
  }

  patchTask($event) {
    let patchedTask = JSON.parse(JSON.stringify(this.task)) as fhir.Task;
    if (this.taskDateObj !== null) {
      const availableToDate = this.taskDateObj[0].toISOString();
      const availableFromDate = moment(availableToDate)
        .subtract(this.admin.orgConfig.proTaskAvailableDaysBefore, "days")
        .toDate()
        .toISOString();
      patchedTask.restriction.period.start = availableFromDate;
      patchedTask.restriction.period.end = availableToDate;
      if (!patchedTask.hasOwnProperty("executionPeriod")) {
        patchedTask.executionPeriod = {
          start: availableFromDate,
          end: availableToDate
        };
      }
      patchedTask.executionPeriod.start = availableFromDate;
      patchedTask.executionPeriod.end = availableToDate;
    }
    if (this.admin.orgConfig.ffProTaskConsultationTypeExtEnabled) {
      if (!Array.isArray(patchedTask.extension)) {
        patchedTask.extension = [];
      }
      patchedTask.extension = [
        ...patchedTask.extension.filter(
          ext =>
            ![
              PRO_TASK_CONSULTATION_TYPE_EXT_URL,
              PRO_TASK_CONSULTATION_TIME_EXT_URL
            ].includes(ext.url)
        ),
        ...this.getTaskExtensions()
      ];
    }
    const patchOperations = compare(this.task, patchedTask);
    this.handlePatch(patchOperations, patchedTask, "EDIT");
  }

  triggerCancelTaskAlert($event) {
    const prompt = this.alertCtrl.create({
      title: this.translate.instant(
        "MANAGE_TASK_MODAL.CANCEL_TASK_ALERT.HEADER"
      ),
      message: this.translate.instant(
        "MANAGE_TASK_MODAL.CANCEL_TASK_ALERT.MESSAGE"
      ),
      cssClass: "large-alert",
      inputs: proTaskCancellationReasonOptions.map(opt => {
        return {
          type: "radio",
          label: this.translate.instant(opt.i18n),
          value: opt.id,
          checked: opt.id === "OTHER" ? true : false
        };
      }),
      buttons: [
        {
          text: this.translate.instant(
            "MANAGE_TASK_MODAL.CANCEL_TASK_ALERT.CANCEL_BTN"
          ),
          handler: data => {
            //
          }
        },
        {
          text: this.translate.instant(
            "MANAGE_TASK_MODAL.CANCEL_TASK_ALERT.OK_BTN"
          ),
          handler: reasonCode => {
            this.cancelTask(event, reasonCode);
          }
        }
      ]
    });
    prompt.present();
  }

  cancelTask($event, reasonCode: proTaskCancellationReasons) {
    const statusMapping: { [key in proTaskCancellationReasons]: string } = {
      CREATED_IN_ERROR: "entered-in-error",
      CONSULTATION_POSTPONED: "cancelled",
      TASK_EXPIRED: "cancelled",
      USE_OF_PRO_REJECTED_BY_PATIENT: "cancelled",
      PATIENT_NO_LONGER_FOLLOWED: "cancelled",
      OTHER: "cancelled"
    };
    const patchedTask = { ...this.task };
    patchedTask.statusReason = {
      coding: [
        {
          system: PRO_TASK_CANCELLATION_REASON_SYSTEM_URL,
          code: reasonCode,
          display: this.translate.instant(
            `PRO_TASK_CANCELLATION_REASON.${reasonCode}`
          )
        }
      ],
      text: reasonCode
    };
    if (!Array.isArray(patchedTask.note)) {
      patchedTask.note = [];
    }
    const newNote: fhir.Annotation = {
      authorReference: {
        reference: `Practitioner/${this.admin.userState.userFhir.id}`,
        display: this.admin.userState.userName
      },
      time: moment(new Date()).toISOString(true),
      text: `[SYSTEM_GENERATED:TASK_CANCELLED_EVENT:${reasonCode}] Task cancelled by author of note`
    };
    patchedTask.note = [...patchedTask.note, ...[newNote]];
    patchedTask.status = statusMapping[reasonCode] || "cancelled";
    const patchOperations = compare(this.task, patchedTask);
    this.handlePatch(patchOperations, patchedTask, "CANCEL");
  }

  triggerDeleteTaskAlert($event) {
    /* Function no loger in use - deprecated by use of triggerCancelTaskAlert */
    const prompt = this.alertCtrl.create({
      title: this.translate.instant(
        "MANAGE_TASK_MODAL.DELETE_TASK_ALERT.HEADER"
      ),
      message: this.translate.instant(
        "MANAGE_TASK_MODAL.DELETE_TASK_ALERT.MESSAGE"
      ),
      buttons: [
        {
          text: this.translate.instant(
            "MANAGE_TASK_MODAL.DELETE_TASK_ALERT.OK_BTN"
          ),
          handler: data => {
            this.deleteTask(event);
          }
        },
        {
          text: this.translate.instant(
            "MANAGE_TASK_MODAL.DELETE_TASK_ALERT.CANCEL_BTN"
          ),
          handler: data => {
            //
          }
        }
      ]
    });
    prompt.present();
  }

  deleteTask($event) {
    /* Function no longer in use - deprecated by use of cancelTask */
    const loading = this.loadingCtrl.create({
      content: this.translate.instant(_i18n_str("GENERIC_UI.DELETING_TASK")),
      dismissOnPageChange: true
    });
    loading.present();
    this.fhir.deleteParentTaskAndAssociatedResources(this.task).subscribe(
      result => {
        loading.dismissAll();
        this.viewCtrl.dismiss();
        this.store.dispatch(
          new PatientActions.UpdateAction({
            fhirId: this.patient.fhirId,
            activeTask: null,
            proTasks: this.patient.proTasks.filter(
              proTask => proTask.task.id !== this.task.id
            )
          })
        );
        const toast = this.toastCtrl.create({
          message: `Konsultationsdato slettet!`,
          duration: 3000,
          position: "top",
          showCloseButton: true
        });
        toast.present();
      },
      // Errors will call this callback instead:
      err => {
        const toast = this.toastCtrl.create({
          message: JSON.stringify(err),
          duration: 20000,
          position: "top",
          showCloseButton: true
        });
        toast.present();
      }
    );
  }

  isEvaluationEnabledForTask() {
    const orgConfig = this.admin.orgConfig;
    if (
      orgConfig.evalProject === "NONE" ||
      orgConfig.evalProjectParticipationStatus !== "ACTIVE" ||
      this.taskDateObj === null ||
      this.taskDateObj.length === 0
    ) {
      return false;
    }
    const taskDate = this.taskDateObj[0].toISOString();
    const cutoffDate = moment("2019-12-02T00:00:00.000Z");
    const difference = moment(taskDate).diff(cutoffDate, "days");
    return difference >= 0;
  }
}



// WEBPACK FOOTER //
// ./src/modals/manage-task.modal/manage-task.modal.ts